import {
  ActionLink,
  ActionLinkColorEnum,
  CatalaFlagIcon,
  ChevronDownIcon,
  EyeIcon,
  EnglandFlagIcon,
  EuskeraFlagIcon,
  ExitIcon,
  FontSizeEnum,
  FontWeightEnum,
  IconButton,
  IconButtonColorEnum,
  IconButtonSizeEnum,
  IconSizeEnum,
  SpainFlagIcon,
  Typography,
} from '@acceso/design-system';
import { useKeycloak } from '@react-keycloak/web';
import clsx from 'clsx';
import { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { storeAppLanguage } from '@/i18n/config';
import { routerManager } from '@/routes/app-routes';
import { MediaManagerRouteEnum } from '@/routes/constants/MediaManagerRouteEnum';
import { useNotificationStore } from '@/stores/notifications';
import { getCurrentLocale } from '@/utils/i18n.utils';

import { useMe } from '../../api/get-me';

interface UserMenuProps {
  close: () => void;
  className?: string;
}

const UserMenu = forwardRef<HTMLDivElement, UserMenuProps>(function UserMenu(
  { close, className },
  ref
) {
  const { data: me } = useMe();
  const { t } = useTranslation();
  const [showLanguageSelector, setShowLanguageSelector] = useState<boolean>(false);

  const { keycloak } = useKeycloak();
  const { addErrorNotification } = useNotificationStore();

  const handleLogout = () => {
    close();
    keycloak.logout().catch((error: unknown) => {
      addErrorNotification(t('Ocurrió un error al cerrar la sesión del usuario'));
      console.error(error);
    });
  };

  const handleProfile = () => {
    routerManager.to(MediaManagerRouteEnum.PROFILE);
    close();
  };

  return (
    <div className={clsx('flex w-full flex-col', className)} ref={ref}>
      <div className="mb-size-8 flex flex-col gap-size-4 bg-white/[0.03] p-size-4">
        <div className="flex items-center justify-between">
          <Typography
            fontSize={FontSizeEnum.Sm}
            weight={FontWeightEnum.Semibold}
            className="overflow-hidden text-ellipsis whitespace-nowrap !text-reverse-accent-neutral-00"
          >
            {me?.first_name} {me?.last_name}
          </Typography>

          <IconButton
            size={IconButtonSizeEnum.SM}
            onClick={handleProfile}
            color={IconButtonColorEnum.NONE_NEUTRAL}
          >
            <EyeIcon size={IconSizeEnum.SM} />
          </IconButton>
        </div>

        <Typography
          fontSize={FontSizeEnum.Sm}
          weight={FontWeightEnum.Regular}
          className="overflow-hidden text-ellipsis whitespace-nowrap !text-reverse-accent-disabled-20"
        >
          {me?.email}
        </Typography>
      </div>
      <div
        className="mb-2 mt-2 flex w-full items-center justify-between"
        onClick={() => setShowLanguageSelector(prev => !prev)}
      >
        <div className="flex items-center">
          {getCurrentLocale() === 'es_ES' && <SpainFlagIcon className="mr-2" />}
          {getCurrentLocale() === 'en_US' && <EnglandFlagIcon className="mr-2" />}
          {getCurrentLocale() === 'ca_CA' && <CatalaFlagIcon className="mr-2" />}
          {getCurrentLocale() === 'eu_EU' && <EuskeraFlagIcon className="mr-2" />}
          <span className="ml-1 text-sm">
            {getCurrentLocale() === 'es_ES' && 'Español'}
            {getCurrentLocale() === 'en_US' && 'English'}
            {getCurrentLocale() === 'ca_CA' && 'Català'}
            {getCurrentLocale() === 'eu_EU' && 'Euskara'}
          </span>
        </div>
        <IconButton
          size={IconButtonSizeEnum.SM}
          onClick={e => {
            e.stopPropagation();
            setShowLanguageSelector(prev => !prev);
          }}
          color={IconButtonColorEnum.NONE_NEUTRAL}
        >
          <ChevronDownIcon />
        </IconButton>
      </div>

      {showLanguageSelector && (
        <>
          <div className="mb-2 mt-2 border-t border-gray-600" />
          <div className="mt-1 flex flex-col">
            <div
              className="mt-1 flex cursor-pointer items-center"
              onClick={() => storeAppLanguage('es_ES')}
            >
              <IconButton
                size={IconButtonSizeEnum.SM}
                onClick={() => storeAppLanguage('es_ES')}
                color={IconButtonColorEnum.NONE_NEUTRAL}
              >
                <SpainFlagIcon className="mr-2" />
              </IconButton>
              <span className="ml-1 flex-grow text-sm">Español</span>
              {getCurrentLocale() === 'es_ES' && <span className="flex text-green-500">✓</span>}
            </div>

            <div className="mb-2 mt-2 border-t border-gray-400" />
            <div
              className="mt-1 flex cursor-pointer items-center"
              onClick={() => storeAppLanguage('en_US')}
            >
              <IconButton
                size={IconButtonSizeEnum.SM}
                onClick={() => storeAppLanguage('en_US')}
                color={IconButtonColorEnum.NONE_NEUTRAL}
              >
                <EnglandFlagIcon className="mr-2" />
              </IconButton>
              <span className="ml-1 flex-grow text-sm">English</span>
              {getCurrentLocale() === 'en_US' && <span className="fle text-green-500">✓</span>}
            </div>

            <div className="mb-2 mt-2 cursor-pointer border-t border-gray-400" />
            <div
              className="mt-1 flex cursor-pointer items-center"
              onClick={() => storeAppLanguage('ca_CA')}
            >
              <IconButton
                size={IconButtonSizeEnum.SM}
                onClick={() => storeAppLanguage('ca_CA')}
                color={IconButtonColorEnum.NONE_NEUTRAL}
              >
                <CatalaFlagIcon className="mr-2" />
              </IconButton>
              <span className="ml-1 flex-grow text-sm">Català</span>
              {getCurrentLocale() === 'ca_CA' && <span className="flex text-green-500">✓</span>}
            </div>

            <div className="mb-2 mt-2 border-t border-gray-400" />
            <div
              className="mt-1 flex cursor-pointer items-center"
              onClick={() => storeAppLanguage('eu_EU')}
            >
              <IconButton
                size={IconButtonSizeEnum.SM}
                onClick={() => storeAppLanguage('eu_EU')}
                color={IconButtonColorEnum.NONE_NEUTRAL}
              >
                <EuskeraFlagIcon className="mr-2" />
              </IconButton>
              <span className="ml-1 flex-grow text-sm">Euskara</span>
              {getCurrentLocale() === 'eu_EU' && <span className="flex text-green-500">✓</span>}
            </div>
          </div>
        </>
      )}
      <div className="mt-6 flex justify-end border-t border-gray-300">
        <ActionLink
          endIcon={ExitIcon}
          color={ActionLinkColorEnum.CUSTOM}
          className="mt-1 text-reverse-accent-neutral-00"
          onClick={handleLogout}
        >
          {t('Cerrar sesión')}
        </ActionLink>
      </div>
    </div>
  );
});

export { UserMenu };
